import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { ACCOUNT_DETAIL_FONT_FAMILY, ACCOUNT_DETAIL_FONT_SX } from "../../../../components/AccountDetailPolicy/accountDetailTypography";
import { formatScore, getPair, getStatusColor } from "./compareUtils";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatCount = (value) => {
  const num = toNumber(value);
  return num == null ? "—" : String(num);
};

const formatCardDate = (scanDate, { short = false } = {}) => {
  const raw = String(scanDate || "").replace("T", " ").trim();
  const datePart = raw.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "";
  const label = `${MONTHS[month - 1]} ${day}`;
  return short ? label : `${label}, ${year}`;
};

const percentChange = (first, second) => {
  const a = toNumber(first);
  const b = toNumber(second);
  if (a == null || b == null) return 0;
  if (a === 0) return b === 0 ? 0 : 100;
  return ((b - a) / Math.abs(a)) * 100;
};

const formatPctChange = (value) => {
  const num = toNumber(value) || 0;
  const abs = Math.abs(num);
  const formatted = Number.isInteger(abs) ? String(abs) : abs.toFixed(1).replace(/\.0$/, "");
  return `${formatted}%`;
};

const formatTenths = (value) => `${Math.abs(toNumber(value) || 0).toFixed(1)}%`;

const signedChangeBadge = (pct) => {
  if (pct > 0) return { label: `↑ +${formatTenths(pct)}`, variant: "up" };
  if (pct < 0) return { label: `↓ -${formatTenths(pct)}`, variant: "down" };
  return { label: "↑ 0%", variant: "zeroUp" };
};

const formatRate = (value) => {
  const num = toNumber(value) || 0;
  const tenths = num.toFixed(1);
  return tenths.endsWith(".0") ? `${Math.round(num)}%` : `${tenths}%`;
};

const resourceSlice = (passed, failed, scanned, runCounts) => {
  const passedCount = Math.max(0, toNumber(passed) || 0);
  const failedCount = Math.max(0, toNumber(failed) || 0);
  const total = Math.max(0, toNumber(scanned) || passedCount + failedCount);
  const explicitManual = runCounts && (runCounts.manual != null || runCounts.MANUAL != null);
  const manual = explicitManual
    ? Math.max(0, toNumber(runCounts.manual ?? runCounts.MANUAL) || 0)
    : Math.max(0, total - passedCount - failedCount);
  return { passed: passedCount, failed: failedCount, manual, total };
};

const ratioLabel = (part, total) => {
  if (!total) return "0%";
  const pct = (part / total) * 100;
  const formatted = pct.toFixed(1).replace(/\.0$/, "");
  return `${formatted}%`;
};

const CARD_SX = {
  ...ACCOUNT_DETAIL_FONT_SX,
  p: 2.25,
  minWidth: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

const VIZ_SX = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: { xs: 120, md: 132 },
  mb: 1.5,
};

const TONES = {
  blue: {
    iconBg: "#eff6ff",
    iconColor: "#2563eb",
    footerBg: "#eff6ff",
    footerColor: "#1e40af",
  },
  green: {
    iconBg: "#ecfdf5",
    iconColor: "#059669",
    footerBg: "#ecfdf5",
    footerColor: "#047857",
  },
  purple: {
    iconBg: "#f5f3ff",
    iconColor: "#7c3aed",
    footerBg: "#f5f3ff",
    footerColor: "#6d28d9",
  },
};

const DatabaseMark = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 20, height: 20, display: "block" }}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <ellipse cx="12" cy="6.2" rx="7" ry="2.6" />
    <path d="M5 6.2v11.2c0 1.45 3.13 2.6 7 2.6s7-1.15 7-2.6V6.2" />
    <path d="M5 12c0 1.45 3.13 2.6 7 2.6s7-1.15 7-2.6" />
  </Box>
);

const ChangeBadge = ({ label, variant = "neutral" }) => {
  const down = variant === "down";
  const up = variant === "up" || variant === "zeroUp";
  return (
    <Box
      sx={{
        px: 1,
        py: 0.35,
        borderRadius: "999px",
        fontSize: "0.6875rem",
        fontWeight: 700,
        fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        bgcolor: down ? "#fef2f2" : up ? "#ecfdf5" : "#f8fafc",
        color: down ? "#b91c1c" : up ? "#047857" : "#475569",
        border: `1px solid ${down ? "#fecaca" : up ? "#a7f3d0" : "#e2e8f0"}`,
      }}
    >
      {label}
    </Box>
  );
};

const ValuePair = ({ first, second, firstDate, secondDate }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "baseline",
      columnGap: 1,
      mb: 1.25,
    }}
  >
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1.35rem", color: "#111827", lineHeight: 1.15 }}>
        {first}
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
        {firstDate}
      </Typography>
    </Box>
    <Typography sx={{ color: "#cbd5e1", fontWeight: 700, fontSize: "1.1rem", px: 0.5, textAlign: "center" }}>→</Typography>
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1.35rem", color: "#111827", lineHeight: 1.15 }}>
        {second}
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
        {secondDate}
      </Typography>
    </Box>
  </Box>
);

const ScoreSparkline = ({ first, second, startLabel, endLabel }) => {
  const a = Math.min(100, Math.max(0, toNumber(first) || 0));
  const b = Math.min(100, Math.max(0, toNumber(second) || 0));
  const w = 320;
  const h = 108;
  const padL = 36;
  const padR = 36;
  const padT = 10;
  const padB = 22;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;
  const y = (v) => padT + (1 - v / 100) * chartH;
  const x1 = padL;
  const x2 = padL + chartW;
  const y1 = y(a);
  const y2 = y(b);
  const yBase = y(0);
  const y50 = y(50);

  return (
    <Box sx={VIZ_SX}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", maxHeight: 132 }}
        aria-hidden="true"
      >
        <text x="0" y={padT + 4} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY}>
          100%
        </text>
        <text x="0" y={y50 + 3} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY}>
          50%
        </text>
        <text x="0" y={yBase + 3} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY}>
          0%
        </text>
        <line x1={padL} y1={y50} x2={x2} y2={y50} stroke="#e2e8f0" strokeDasharray="3 4" />
        <polygon
          points={`${x1},${yBase} ${x1},${y1} ${x2},${y2} ${x2},${yBase}`}
          fill="#dbeafe"
          opacity="0.85"
        />
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#3b82f6" strokeWidth="2.5" />
        <circle cx={x1} cy={y1} r="4.5" fill="#3b82f6" />
        <circle cx={x2} cy={y2} r="4.5" fill="#3b82f6" />
        <text x={x1} y={h - 4} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY} textAnchor="start">
          {startLabel}
        </text>
        <text x={x2} y={h - 4} fill="#64748b" fontSize="10" fontWeight="500" fontFamily={ACCOUNT_DETAIL_FONT_FAMILY} textAnchor="end">
          {endLabel}
        </text>
      </svg>
    </Box>
  );
};

const COLUMN_SX = {
  flex: "1 1 0",
  minWidth: 0,
  maxWidth: 72,
  display: "flex",
  justifyContent: "center",
};

const ResourceColumn = ({ color, count, percent, share }) => (
  <Box sx={{ ...COLUMN_SX, height: "100%", alignItems: "flex-end" }}>
    <Box
      sx={{
        position: "relative",
        width: "62%",
        maxWidth: 32,
        minWidth: 16,
        height: `${share}%`,
        minHeight: share > 0 ? 4 : 0,
        borderRadius: "6px 6px 0 0",
        bgcolor: color,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: "calc(100% + 4px)",
          transform: "translateX(-50%)",
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#111827", lineHeight: 1.15 }}>
          {formatCount(count)}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color, lineHeight: 1.15 }}>
          {percent}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ResourceBars = ({ passed, failed, manual, total }) => {
  const safeTotal = total > 0 ? total : 0;
  const failedColor = getStatusColor("FAILED");
  const manualColor = getStatusColor("MANUAL");
  const segments = [
    { color: "#16a34a", label: "Passed", count: Math.max(0, passed) },
    { color: failedColor, label: "Failed", count: Math.max(0, failed) },
    { color: manualColor, label: "Manual", count: Math.max(0, manual) },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        px: 0.25,
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Box sx={{ width: "100%", pt: "36px" }}>
          <Box
            sx={{
              width: "100%",
              height: 92,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: 1,
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            {segments.map((segment) => (
              <ResourceColumn
                key={segment.label}
                color={segment.color}
                count={segment.count}
                percent={ratioLabel(segment.count, safeTotal)}
                share={safeTotal > 0 ? Math.min(100, (segment.count / safeTotal) * 100) : 0}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", gap: 1, mt: 0.75 }}>
          {segments.map((segment) => (
            <Typography
              key={segment.label}
              sx={{ ...COLUMN_SX, fontSize: "0.75rem", fontWeight: 700, color: "#111827", lineHeight: 1.2, textAlign: "center" }}
            >
              {segment.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const PASS_SEGMENTS = 13;

const PassDots = ({ rate }) => {
  const filled = Math.max(
    0,
    Math.min(PASS_SEGMENTS, Math.round(((toNumber(rate) || 0) / 100) * PASS_SEGMENTS))
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${PASS_SEGMENTS}, minmax(0, 1fr))`,
        gap: "3px",
        width: "100%",
      }}
      aria-hidden="true"
    >
      {Array.from({ length: PASS_SEGMENTS }).map((_, index) => (
        <Box
          key={index}
          sx={{
            height: 14,
            borderRadius: "3px",
            bgcolor: index < filled ? "#7c3aed" : "#e5e7eb",
          }}
        />
      ))}
    </Box>
  );
};

const CheckPeriod = ({ date, passed, total, rate }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      columnGap: 1.5,
      rowGap: 0.75,
      alignItems: "center",
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#64748b", lineHeight: 1.2 }}>
      {date}
    </Typography>
    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827", lineHeight: 1.2, textAlign: "right", whiteSpace: "nowrap" }}>
      {formatCount(passed)} / {formatCount(total)}
    </Typography>
    <PassDots rate={rate} />
    <Typography sx={{ fontWeight: 500, fontSize: "0.8rem", color: "#64748b", lineHeight: 1.2, textAlign: "right", whiteSpace: "nowrap" }}>
      {formatRate(rate)}
    </Typography>
  </Box>
);

const OverviewCard = ({ tone, icon, title, subtitle, badge, children, footerIcon, footer }) => (
  <Box sx={CARD_SX}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "flex-start",
        columnGap: 1,
        mb: 1.75,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: tone.iconBg,
            color: tone.iconColor,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827", lineHeight: 1.2, letterSpacing: "0.02em" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500, mt: 0.2 }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{badge}</Box>
    </Box>
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {children}
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 1,
        px: 1.25,
        py: 1,
        borderRadius: "10px",
        bgcolor: tone.footerBg,
        color: tone.footerColor,
      }}
    >
      <Box sx={{ mt: "1px", display: "inline-flex" }}>{footerIcon}</Box>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, lineHeight: 1.45, color: tone.footerColor, textAlign: "center" }}>
        {footer}
      </Typography>
    </Box>
  </Box>
);

const CompareSummaryCards = ({ result, baselineDate, compareDate }) => {
  const model = useMemo(() => {
    if (!result) return null;
    const firstSummary = result.firstRun?.summary || {};
    const secondSummary = result.secondRun?.summary || {};
    const summary = result.summary || {};

    const score = getPair(summary.score, firstSummary.score, secondSummary.score);
    const scanned = getPair(
      summary.resourceCounts?.scanned,
      firstSummary.resourceCounts?.scanned,
      secondSummary.resourceCounts?.scanned
    );
    const passedResources = getPair(
      summary.resourceCounts?.passed,
      firstSummary.resourceCounts?.passed,
      secondSummary.resourceCounts?.passed
    );
    const failedResources = getPair(
      summary.resourceCounts?.failed,
      firstSummary.resourceCounts?.failed,
      secondSummary.resourceCounts?.failed
    );
    const passedChecks = getPair(
      summary.checkStatusCounts?.PASSED,
      firstSummary.checkStatusCounts?.PASSED,
      secondSummary.checkStatusCounts?.PASSED
    );
    const failedChecks = getPair(
      summary.checkStatusCounts?.FAILED,
      firstSummary.checkStatusCounts?.FAILED,
      secondSummary.checkStatusCounts?.FAILED
    );

    const baselineResources = resourceSlice(
      passedResources.first,
      failedResources.first,
      scanned.first,
      firstSummary.resourceCounts
    );
    const compareResources = resourceSlice(
      passedResources.second,
      failedResources.second,
      scanned.second,
      secondSummary.resourceCounts
    );
    const baselinePassedChecks = Math.max(0, toNumber(passedChecks.first) || 0);
    const baselineFailedChecks = Math.max(0, toNumber(failedChecks.first) || 0);
    const baselineTotalChecks = baselinePassedChecks + baselineFailedChecks;
    const baselinePassRate = baselineTotalChecks
      ? (baselinePassedChecks / baselineTotalChecks) * 100
      : 0;
    const comparePassedChecks = Math.max(0, toNumber(passedChecks.second) || 0);
    const compareFailedChecks = Math.max(0, toNumber(failedChecks.second) || 0);
    const compareTotalChecks = comparePassedChecks + compareFailedChecks;
    const passRate = compareTotalChecks
      ? (comparePassedChecks / compareTotalChecks) * 100
      : 0;

    const scoreDelta = toNumber(score.delta) || 0;
    const scannedDelta = toNumber(scanned.delta) || 0;
    const passedDelta = toNumber(passedChecks.delta) || 0;
    const scannedPct = percentChange(scanned.first, scanned.second);
    const passedPct = percentChange(passedChecks.first, passedChecks.second);

    return {
      score,
      scoreDelta,
      scanned,
      scannedDelta,
      scannedPct,
      baselineResources,
      compareResources,
      passedChecks,
      passedDelta,
      passedPct,
      baselinePassedChecks,
      baselineTotalChecks,
      baselinePassRate,
      comparePassedChecks,
      compareTotalChecks,
      passRate,
    };
  }, [result]);

  if (!model) return null;

  const firstDate = formatCardDate(baselineDate);
  const secondDate = formatCardDate(compareDate);
  const firstShort = formatCardDate(baselineDate, { short: true });
  const secondShort = formatCardDate(compareDate, { short: true });

  const scoreBadge =
    model.scoreDelta === 0
      ? { label: "No Change", variant: "neutral" }
      : {
          label: `${model.scoreDelta > 0 ? "↑" : "↓"} ${formatPctChange(Math.abs(model.scoreDelta))}`,
          variant: model.scoreDelta > 0 ? "up" : "down",
        };

  const scannedBadge = signedChangeBadge(model.scannedPct);
  const passedBadge = signedChangeBadge(model.passedPct);

  const scoreFooter =
    model.scoreDelta === 0
      ? "Security score remains the same as the previous period."
      : model.scoreDelta > 0
        ? `Security score improved by ${formatPctChange(Math.abs(model.scoreDelta))} compared to the previous period.`
        : `Security score declined by ${formatPctChange(Math.abs(model.scoreDelta))} compared to the previous period.`;

  const scannedFooter =
    model.scannedDelta === 0
      ? "No change in resources scanned compared to the previous period."
      : model.scannedDelta > 0
        ? `${formatCount(model.scannedDelta)} more resources scanned (+${formatTenths(model.scannedPct)}) compared to the previous period.`
        : `${formatCount(Math.abs(model.scannedDelta))} fewer resources scanned (-${formatTenths(model.scannedPct)}) compared to the previous period.`;

  const baselineRateLabel = formatRate(model.baselinePassRate);
  const compareRateLabel = formatRate(model.passRate);
  const rateUnchanged = baselineRateLabel === compareRateLabel;
  const countSentence =
    model.passedDelta > 0
      ? `${formatCount(model.passedDelta)} more checks passed (+${formatTenths(model.passedPct)}).`
      : model.passedDelta < 0
        ? `${formatCount(Math.abs(model.passedDelta))} fewer checks passed (-${formatTenths(model.passedPct)}).`
        : "";
  const rateSentence = rateUnchanged
    ? ""
    : model.passRate > model.baselinePassRate
      ? `Pass rate increased from ${baselineRateLabel} to ${compareRateLabel}.`
      : `Pass rate decreased from ${baselineRateLabel} to ${compareRateLabel}.`;
  const passedFooter =
    model.passedDelta === 0 && rateUnchanged
      ? model.compareTotalChecks > 0 && model.comparePassedChecks === model.compareTotalChecks
        ? "All checks passed. No change from the previous period."
        : "Checks passed remains the same as the previous period."
      : [countSentence, rateSentence].filter(Boolean).join(" ");

  return (
    <Box
      sx={{
        mb: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
        gap: 2,
        alignItems: "stretch",
        gridAutoRows: { lg: "1fr" },
      }}
    >
      <OverviewCard
        tone={TONES.blue}
        icon={<ShieldOutlinedIcon sx={{ fontSize: 20 }} />}
        title="Security Score"
        subtitle="Overall security posture"
        badge={<ChangeBadge {...scoreBadge} />}
        footerIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
        footer={scoreFooter}
      >
        <ValuePair
          first={formatScore(model.score.first)}
          second={formatScore(model.score.second)}
          firstDate={firstDate}
          secondDate={secondDate}
        />
        <ScoreSparkline
          first={model.score.first}
          second={model.score.second}
          startLabel={firstShort}
          endLabel={secondShort}
        />
      </OverviewCard>

      <OverviewCard
        tone={TONES.green}
        icon={<DatabaseMark />}
        title="Resources Scanned"
        subtitle="Total resources analyzed"
        badge={<ChangeBadge {...scannedBadge} />}
        footerIcon={<BarChartOutlinedIcon sx={{ fontSize: 16 }} />}
        footer={scannedFooter}
      >
        <ValuePair
          first={formatCount(model.scanned.first)}
          second={formatCount(model.scanned.second)}
          firstDate={firstDate}
          secondDate={secondDate}
        />
        <Box
          sx={{
            ...VIZ_SX,
            flexDirection: "row",
            alignItems: "stretch",
            justifyContent: "stretch",
          }}
        >
          <ResourceBars
            passed={model.baselineResources.passed}
            failed={model.baselineResources.failed}
            manual={model.baselineResources.manual}
            total={model.baselineResources.total}
          />
          <Box sx={{ width: "1px", flexShrink: 0, mx: 1, my: 0.5, bgcolor: "#e2e8f0" }} />
          <ResourceBars
            passed={model.compareResources.passed}
            failed={model.compareResources.failed}
            manual={model.compareResources.manual}
            total={model.compareResources.total}
          />
        </Box>
      </OverviewCard>

      <OverviewCard
        tone={TONES.purple}
        icon={<TaskAltIcon sx={{ fontSize: 20 }} />}
        title="Checks Passed"
        subtitle="Security checks completed"
        badge={<ChangeBadge {...passedBadge} />}
        footerIcon={<StarOutlineIcon sx={{ fontSize: 16 }} />}
        footer={passedFooter}
      >
        <ValuePair
          first={formatCount(model.passedChecks.first)}
          second={formatCount(model.passedChecks.second)}
          firstDate={firstDate}
          secondDate={secondDate}
        />
        <Box sx={{ ...VIZ_SX, flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 2.5 }}>
          <CheckPeriod
            date={firstDate}
            passed={model.baselinePassedChecks}
            total={model.baselineTotalChecks}
            rate={model.baselinePassRate}
          />
          <CheckPeriod
            date={secondDate}
            passed={model.comparePassedChecks}
            total={model.compareTotalChecks}
            rate={model.passRate}
          />
        </Box>
      </OverviewCard>
    </Box>
  );
};

export default CompareSummaryCards;
